import FeeStructureDelegate from '../delegates/FeeStructure.delegate';
import VolumeTiersPanelController from './VolumeTiersPanel.controller';
import MemberPromosController from './MemberPromos.controller';
import TabController from './Tab.controller';
import Inherit from '../../../core/inherit';
import {
    DEFAULT_FEE_ENABLED,
    DEFAULT_FEE_RULES,
    DEFAULT_FEE_STRUCTURE_STATE
} from '../configuration.constants';
import { clone } from '../../../utils/json';

/**
 * View-model for the "Fee Structure" tab. Its render slice lives in the
 * host component's `state.tabs.feeStructure` (via {@link TabController}).
 *
 * Instantiates the `VolumeTiersPanelController` sub-controller, which owns
 * its own host slice (`state.tabs.volumeTiers`). After each add/remove of
 * a volume tier rule the controller passes the updated rules array
 * explicitly to `reconcileOrderedIds` so the ordering slice stays in sync
 * in the same React batch — no in-flight state reading needed.
 */
class FeeStructureController extends Inherit(TabController, FeeStructureDelegate) {
    constructor(page) {
        super(page, 'feeStructure');
        this.snapshot = null;
        this.volumeTiers = new VolumeTiersPanelController(this);
        // Deliberately NOT part of captureSnapshot/hasChanges/getValues below —
        // memberPromos owns its own load/save lifecycle against its own REST
        // endpoints (see MemberPromosController's class comment), fully
        // isolated from this tab's Save button.
        this.memberPromos = new MemberPromosController(this);
    }

    /** Default render slice the host seeds `state.tabs.feeStructure` with. */
    getInitialState() {
        return clone(DEFAULT_FEE_STRUCTURE_STATE);
    }

    // --- Snapshot / change detection -----------------------------------

    captureSnapshot(source = this.state) {
        this.snapshot = clone({
            fee: source.fee,
            fee_policy: source.fee_policy,
            fee_rules: source.fee_rules,
            fee_enabled: source.fee_enabled
        });
    }

    hasChanges() {
        if (!this.snapshot) return false;
        if (this.state.fee !== this.snapshot.fee) return true;
        if (JSON.stringify(this.state.fee_policy) !== JSON.stringify(this.snapshot.fee_policy)) return true;
        if (JSON.stringify(this.state.fee_rules) !== JSON.stringify(this.snapshot.fee_rules)) return true;
        if (JSON.stringify(this.state.fee_enabled) !== JSON.stringify(this.snapshot.fee_enabled)) return true;
        return false;
    }

    // --- Volume tier rules (CRUD) --------------------------------------

    addVolumeTierRule() {
        const { volumeTierRules } = this.state.fee_rules;
        const newId = volumeTierRules.length > 0 ? Math.max(...volumeTierRules.map((r) => r.id)) + 1 : 1;
        // Default the new tier to start at the highest existing `toAmount`
        // so adjacent tiers can share a boundary (e.g. 0-50, then 50-100).
        const highestTo = volumeTierRules.reduce((max, r) => {
            const value = Number(r.toAmount);
            return Number.isFinite(value) && value > max ? value : max;
        }, -1);
        const fromAmount = highestTo >= 0 ? highestTo : 0;
        const toAmount = fromAmount + 50;
        const newRule = { id: newId, fromAmount, toAmount, fee: 1.0 };

        this.appendFeeRule('volumeTierRules', newRule);
        // Pass the post-add rules array explicitly so reconcileOrderedIds
        // runs in the same batch without needing to read in-flight state.
        this.volumeTiers.reconcileOrderedIds([...volumeTierRules, newRule]);
    }

    removeVolumeTierRule(ruleId) {
        const updatedRules = this.state.fee_rules.volumeTierRules.filter((r) => r.id !== ruleId);
        this.removeFeeRule('volumeTierRules', ruleId);
        this.volumeTiers.reconcileOrderedIds(updatedRules);
    }

    updateVolumeTierRule(ruleId, field, value) {
        this.updateFeeRule('volumeTierRules', ruleId, field, value);
    }

    // --- Product type rules (CRUD) -------------------------------------

    addProductTypeRule() {
        const productTypeRules = this.state.fee_rules.productTypeRules || [];
        const newId = productTypeRules.length > 0 ? Math.max(...productTypeRules.map((r) => r.id)) + 1 : 1;
        this.appendFeeRule('productTypeRules', {
            id: newId,
            productType: 'basic-checking',
            fee: 2.0
        });
    }

    removeProductTypeRule(ruleId) {
        this.removeFeeRule('productTypeRules', ruleId);
    }

    updateProductTypeRule(ruleId, field, value) {
        this.updateFeeRule('productTypeRules', ruleId, field, value);
    }

    // --- Free advance rules (CRUD) -------------------------------------

    addFreeAdvanceRule() {
        const freeAdvanceRules = this.state.fee_rules.freeAdvanceRules || [];
        const newId = freeAdvanceRules.length > 0 ? Math.max(...freeAdvanceRules.map((r) => r.id)) + 1 : 1;
        this.appendFeeRule('freeAdvanceRules', {
            id: newId,
            count: 1,
            condition: 'first-advance'
        });
    }

    removeFreeAdvanceRule(ruleId) {
        this.removeFeeRule('freeAdvanceRules', ruleId);
    }

    updateFreeAdvanceRule(ruleId, field, value) {
        this.updateFeeRule('freeAdvanceRules', ruleId, field, value);
    }

    // --- Transforms -----------------------------------------------------

    transformPolicyToSnakeCase(feePolicy = {}) {
        return {
            standard_free_advance_time: feePolicy.standardFreeAdvanceTime ?? null,
            standard_free_advance_unit: feePolicy.standardFreeAdvanceUnit || null,
            fee_instance: feePolicy.feeInstance || null,
            fee_strategy: feePolicy.feeStrategy || null
        };
    }

    transformPolicyToCamelCase(feePolicy = {}) {
        return {
            standardFreeAdvanceTime: feePolicy.standard_free_advance_time ?? 1,
            standardFreeAdvanceUnit: feePolicy.standard_free_advance_unit || 'business-days',
            feeInstance: feePolicy.fee_instance || 'on-advance',
            feeStrategy: feePolicy.fee_strategy || 'on-top-of-advance'
        };
    }

    transformRulesToSnakeCase(feeRules = {}) {
        return {
            volume_tier_rules: feeRules.volumeTierRules || [],
            product_type_rules: feeRules.productTypeRules || [],
            free_advance_rules: feeRules.freeAdvanceRules || []
        };
    }

    transformRulesToCamelCase(feeRules = {}) {
        return {
            volumeTierRules: feeRules.volume_tier_rules || DEFAULT_FEE_RULES.volumeTierRules,
            productTypeRules: feeRules.product_type_rules || DEFAULT_FEE_RULES.productTypeRules,
            freeAdvanceRules: feeRules.free_advance_rules || DEFAULT_FEE_RULES.freeAdvanceRules
        };
    }

    // --- Lifecycle (orchestrator API) ----------------------------------

    /**
     * Pure: computes and returns this tab's initial slice from the FI
     * payload without touching React state. Seeds the snapshot.
     * The orchestrator derives the volumeTiers slice separately via
     * `volumeTiers.applyFromFi(feeStructureSlice.fee_rules.volumeTierRules)`.
     */
    applyFromFi(fiData) {
        const next = this.getInitialState();

        if (fiData?.fee !== undefined) {
            const numericFee = typeof fiData.fee === 'string' ? parseFloat(fiData.fee) : fiData.fee;
            next.fee = isNaN(numericFee) ? 0 : numericFee;
        }
        if (fiData?.fee_policy) {
            next.fee_policy = this.transformPolicyToCamelCase(fiData.fee_policy);
        }
        if (fiData?.fee_rules) {
            next.fee_rules = this.transformRulesToCamelCase(fiData.fee_rules);
        }
        if (fiData?.fee_enabled) {
            next.fee_enabled = {
                instantFee: fiData.fee_enabled.instantFee ?? DEFAULT_FEE_ENABLED.instantFee,
                volumeTiers: fiData.fee_enabled.volumeTiers ?? DEFAULT_FEE_ENABLED.volumeTiers,
                productTypes: fiData.fee_enabled.productTypes ?? DEFAULT_FEE_ENABLED.productTypes,
                freeAdvances: fiData.fee_enabled.freeAdvances ?? DEFAULT_FEE_ENABLED.freeAdvances
            };
        }

        this.captureSnapshot(next);
        return next;
    }

    /**
     * Returns the slice the orchestrator drops into the submit-all payload.
     */
    getValues() {
        return {
            fee: this.state.fee,
            fee_policy: this.transformPolicyToSnakeCase(this.state.fee_policy),
            fee_rules: this.transformRulesToSnakeCase(this.state.fee_rules),
            fee_enabled: this.state.fee_enabled
        };
    }

    // --- Submit / cancel -----------------------------------------------

    async handleSubmit() {
        if (!this.page.canModifyConfiguration()) {
            return;
        }
        try {
            this.page.setLoading(true);
            const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
            if (!aba) {
                this.page.props.notificationApi.error({
                    message: 'Error',
                    description: 'Unable to find FI ABA'
                });
                return;
            }

            const response = await this.page.data.updateFI(aba, { fi_info: this.getValues() });

            if (response) {
                this.captureSnapshot();
                this.page.props.notificationApi.success({
                    message: 'Success',
                    description: 'Fee structure configuration updated successfully'
                });
            }
        } catch (error) {
            console.error('Error updating fee:', error);
            this.page.props.notificationApi.error({
                message: 'Update Failed',
                description: 'Failed to update fee structure configuration'
            });
        } finally {
            this.page.setLoading(false);
        }
    }

    handleCancel() {
        if (!this.snapshot) return;
        this.replaceState(clone(this.snapshot));
        // Reconcile ordering after the revert using the snapshot's rules.
        this.volumeTiers.reconcileOrderedIds(
            this.snapshot.fee_rules?.volumeTierRules || []
        );
    }

    // --- View props -----------------------------------------------------

    getViewProps() {
        const { fee, fee_policy = {}, fee_rules = {}, fee_enabled = {} } = this.state;
        const { firstTimeSetup, loading, canModify } = this.page.state;

        return {
            values: {
                fee,
                feeInstance: fee_policy.feeInstance,
                feeStrategy: fee_policy.feeStrategy,
                standardFreeAdvanceTime: fee_policy.standardFreeAdvanceTime,
                standardFreeAdvanceUnit: fee_policy.standardFreeAdvanceUnit
            },
            rules: fee_rules,
            enabled: fee_enabled,
            handlers: {
                setFee: (v) => this.setFee(v),
                setFeeInstance: (v) => this.setFeePolicyField('feeInstance', v),
                setFeeStrategy: (v) => this.setFeePolicyField('feeStrategy', v),
                setStandardFreeAdvanceTime: (v) => this.setFeePolicyField('standardFreeAdvanceTime', v ?? 1),
                setStandardFreeAdvanceUnit: (v) => this.setFeePolicyField('standardFreeAdvanceUnit', v),
                toggleFeeSection: (k) => this.toggleFeeSection(k),
                addVolumeTierRule: this.addVolumeTierRule.bind(this),
                removeVolumeTierRule: this.removeVolumeTierRule.bind(this),
                updateVolumeTierRule: this.updateVolumeTierRule.bind(this),
                addProductTypeRule: this.addProductTypeRule.bind(this),
                removeProductTypeRule: this.removeProductTypeRule.bind(this),
                updateProductTypeRule: this.updateProductTypeRule.bind(this),
                addFreeAdvanceRule: this.addFreeAdvanceRule.bind(this),
                removeFreeAdvanceRule: this.removeFreeAdvanceRule.bind(this),
                updateFreeAdvanceRule: this.updateFreeAdvanceRule.bind(this)
            },
            // Pre-cooked bundles for sub-controllers — the dumb panels
            // only have to spread these onto their root component.
            panels: {
                volumeTiers: this.volumeTiers.getViewProps(),
                memberPromos: this.memberPromos.getViewProps()
            },
            // Slice for the side `AdvanceSummaryCard`. The chart bundle
            // is pre-cooked by the volume-tiers sub-controller so the
            // chart component itself stays purely presentational.
            sideCard: {
                fee,
                fee_policy,
                feeByVolume: this.volumeTiers.getChart()
            },
            loading,
            readOnly: !canModify,
            disabledSave: !canModify || (!firstTimeSetup && !this.hasChanges()),
            submitText: firstTimeSetup ? 'Submit All' : 'Save',
            confirmBeforeSubmit: !firstTimeSetup,
            onSubmit: firstTimeSetup
                ? this.page.handleContinue.bind(this.page)
                : this.handleSubmit.bind(this),
            onCancel: firstTimeSetup
                ? this.page.handleCancel.bind(this.page)
                : this.handleCancel.bind(this)
        };
    }
}

export default FeeStructureController;
